import React, { useContext, useEffect, useState } from 'react';
import {
  IPaymentTask,
  IPickupTask,
} from '../../../interfaces/delivery-company/tasks';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { isMobile, isTablet } from 'react-device-detect';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import SnackBar from '../../common/snack-bar/snack-bar';
import { PaymentTaskState } from '../../../enums/tasks';
import CustomDialog from '../../common/custom-dialog/custom-dialog';

interface ConfirmPaymentDialogProps {
  task: IPaymentTask;
  onClose: () => void;
  onSuccess: () => void;
}

function ConfirmPaymentDialog({
  task,
  onClose,
  onSuccess,
}: ConfirmPaymentDialogProps) {
  const [sendingSecretCode, setSendingSecretCode] = useState(false);
  const [secretCodeSent, setSecretCodeSent] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');

  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState('');

  const [snackBarMessage, setSnackBarMessage] = useState<timestampedMessage>();

  const { user, config } = useContext(AuthContext);

  const confirmPayment = async () => {
    setVerifyingPayment(true);
    setSnackBarMessage(undefined);
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${task.id}/`;
      const response = await axios.patch(
        endpoint,
        {
          secret_code: verificationCode,
          status: PaymentTaskState.COMPLETED,
        },
        config
      );
      setMessage('Payment Confirmed Successfully');
      setSnackBarMessage({
        content: 'Payment Confirmed',
        level: 'success',
        timestamp: Date.now(),
      });
      setResponse(response);
      onSuccess();
      onClose();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setMessage('Failed to verify payment: ' + e.response?.statusText);
        setSnackBarMessage({
          content: 'Failed to verify payment',
          level: 'error',
          timestamp: Date.now(),
        });
        setResponse(e.response);
      } else {
        setSnackBarMessage({
          content: 'Failed to verify payment',
          level: 'error',
          timestamp: Date.now(),
        });
        setMessage('Failed to verify payment. Please try again.');
      }
    }

    setVerifyingPayment(false);
  };

  const sendSecret = async () => {
    setSendingSecretCode(true);
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${task.id}/send-secret`;
      const response = await axios.post(endpoint, {}, config);
      console.log(response);
      setMessage('Secret code sent to merchant successfully');
      setResponse(response);
      setSecretCodeSent(true);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setMessage('Failed to send secret code: ' + e.response?.statusText);
        setResponse(e.response);
      } else {
        setMessage('Failed to send secret code. Please try again.');
      }
    }

    setSendingSecretCode(false);
  };

  useEffect(() => {
    sendSecret();
  }, [task.id]);

  return (
    <CustomDialog open={true} fullScreen={false} onClose={onClose} fullWidth>
      <DialogTitle> Confirming Payment</DialogTitle>

      <DialogContent>
        {sendingSecretCode ? (
          <>
            Sending verification code to merchant
            <CircularProgress />
          </>
        ) : (
          <>
            {secretCodeSent ? (
              <>
                A verification code has been sent to{' '}
                {task.merchant_location?.contact_person} at{' '}
                {task.merchant_location?.contact_phone}.
                <br />
                <br />
                {verifyingPayment ? (
                  <>
                    {' '}
                    Verifying your payment... <br /> <CircularProgress />
                  </>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      confirmPayment();
                    }}
                  >
                    <TextField
                      autoComplete="off"
                      fullWidth
                      placeholder="Enter verification code"
                      onChange={(e) => setVerificationCode(e.target.value)}
                      value={verificationCode}
                      type="password"
                      label="Verification Code"
                      required
                      autoSave="off"
                    />
                    <br />
                    <br />

                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                        gap: 5,
                      }}
                    >
                      <Button variant="contained" type="submit">
                        {' '}
                        Confirm{' '}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={sendSecret}
                      >
                        {' '}
                        Send New Code{' '}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <>
                Failed to send verification code <br />{' '}
                <Alert severity="info">
                  You can contact the accounting department upon payment to
                  approve the operation
                </Alert>
              </>
            )}
          </>
        )}
      </DialogContent>

      {snackBarMessage && (
        <SnackBar
          message={snackBarMessage.content}
          level={snackBarMessage.level}
          key={snackBarMessage.timestamp}
        />
      )}
    </CustomDialog>
  );
}

export default ConfirmPaymentDialog;
